import styled from "@emotion/styled";
import { keyframes, Theme } from "@emotion/react";
import { MDSDotPulseLoaderKind, MDSDotPulseLoaderSize } from "./dot-pulse-loader.types";

// Helper functions for theming
const getDotPulseColor = (kind: MDSDotPulseLoaderKind, theme: Theme): string => {
  switch (kind) {
    case MDSDotPulseLoaderKind.Light:
      return theme.colors.grey.x0;
    case MDSDotPulseLoaderKind.Brand:
      return theme.colors.primary.x500;
    case MDSDotPulseLoaderKind.Dark:
    default:
      return theme.colors.grey.x700;
  }
};

const getSizeValues = (size: MDSDotPulseLoaderSize) => {
  switch (size) {
    case MDSDotPulseLoaderSize.Small:
      return {
        DOT_PULSE_MAX_SIZE: 2,
        DOT_PULSE_MIN_SIZE: 1.5,
      };
    case MDSDotPulseLoaderSize.Medium:
    default:
      return {
        DOT_PULSE_MAX_SIZE: 3,
        DOT_PULSE_MIN_SIZE: 2,
      };
  }
};

// Define keyframe factories
const createDotPulseAnimation = (
  DOT_PULSE_MAX_SIZE: number,
  DOT_PULSE_MIN_SIZE: number,
  dotPulseColor: string,
  offset: number = 9999
) => keyframes`
  0% {
    box-shadow: ${offset}px 0 0 -${DOT_PULSE_MAX_SIZE}px ${dotPulseColor};
  }
  30% {
    box-shadow: ${offset}px 0 0 ${DOT_PULSE_MIN_SIZE}px ${dotPulseColor};
  }
  60%, 100% {
    box-shadow: ${offset}px 0 0 -${DOT_PULSE_MAX_SIZE}px ${dotPulseColor};
  }
`;

export const StyledWrapper = styled.span`
  width: 10px;
  height: 10px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  position: relative;
`;

export const StyledDotLoader = styled.span<{
  kind: MDSDotPulseLoaderKind;
  size: MDSDotPulseLoaderSize;
}>`
  ${({ kind, size, theme }) => {
    const dotPulseColor = getDotPulseColor(kind, theme);
    const { DOT_PULSE_MAX_SIZE, DOT_PULSE_MIN_SIZE } = getSizeValues(size);

    const DOT_PULSE_OFFSET = 9999;
    const DOT_PULSE_SPACING = DOT_PULSE_MAX_SIZE * 3 - 2;
    const BEFORE_DOT_PULSE_OFFSET = DOT_PULSE_OFFSET - DOT_PULSE_SPACING - DOT_PULSE_MAX_SIZE * 3;
    const AFTER_DOT_PULSE_OFFSET = DOT_PULSE_OFFSET + DOT_PULSE_SPACING + DOT_PULSE_MAX_SIZE + 5;

    const dotPulseAnimation = createDotPulseAnimation(
      DOT_PULSE_MAX_SIZE,
      DOT_PULSE_MIN_SIZE,
      dotPulseColor
    );
    const dotPulseBeforeAnimation = createDotPulseAnimation(
      DOT_PULSE_MAX_SIZE,
      DOT_PULSE_MIN_SIZE,
      dotPulseColor,
      BEFORE_DOT_PULSE_OFFSET
    );
    const dotPulseAfterAnimation = createDotPulseAnimation(
      DOT_PULSE_MAX_SIZE,
      DOT_PULSE_MIN_SIZE,
      dotPulseColor,
      AFTER_DOT_PULSE_OFFSET
    );

    return {
      position: "absolute",
      left: `${-(DOT_PULSE_OFFSET - DOT_PULSE_MAX_SIZE + 2)}px`,
      width: `${DOT_PULSE_MAX_SIZE * 2}px`,
      height: `${DOT_PULSE_MAX_SIZE * 2}px`,
      borderRadius: `${DOT_PULSE_MAX_SIZE}px`,
      backgroundColor: dotPulseColor,
      color: dotPulseColor,
      boxShadow: `${DOT_PULSE_OFFSET}px 0 0 -${DOT_PULSE_MAX_SIZE}px ${dotPulseColor}`,
      animation: `${dotPulseAnimation} 1.5s infinite linear`,
      animationDelay: "0.25s",

      "&::before, &::after": {
        content: "''",
        display: "inline-block",
        position: "absolute",
        top: "0",
        width: `${DOT_PULSE_MAX_SIZE * 2}px`,
        height: `${DOT_PULSE_MAX_SIZE * 2}px`,
        borderRadius: `${DOT_PULSE_MAX_SIZE}px`,
        backgroundColor: dotPulseColor,
        color: dotPulseColor,
      },

      "&::before": {
        boxShadow: `${BEFORE_DOT_PULSE_OFFSET}px 0 0 -${DOT_PULSE_MAX_SIZE}px ${dotPulseColor}`,
        animation: `${dotPulseBeforeAnimation} 1.5s infinite linear`,
        animationDelay: "0s",
      },

      "&::after": {
        boxShadow: `${AFTER_DOT_PULSE_OFFSET}px 0 0 -${DOT_PULSE_MAX_SIZE}px ${dotPulseColor}`,
        animation: `${dotPulseAfterAnimation} 1.5s infinite linear`,
        animationDelay: "0.5s",
      },
    };
  }}
`;
